// ═══════════════════ PLATFORM TIMEZONE CONVENTION ═══════════════════
// Stored schedule times (sessions.start/end, events.start_at/end_at,
// bookings, deadlines…) are NAIVE IST wall-clock strings —
// 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm'. IST has no DST so this is a
// lossless, unambiguous representation of an instant.
//
// RULES for all future code:
//   • parse stored strings with parseAppDate() — NEVER `new Date(str)`
//   • display with the helpers below — they render in the VIEWER'S
//     local timezone with a timezone label (IST users see "… IST")
//   • datetime-local inputs: value={storedToLocalInput(v)} and
//     onChange={(e) => localInputToStored(e.target.value)} — admins
//     pick times in their own local time, stored as IST wall-clock
//   • compare wall-clock strings against nowWall(), not new Date()
// Mirror of this module on mobile: founder-os-mobile/src/lib/format.js
// Backend counterpart: backend/time_utils.py
// ═════════════════════════════════════════════════════════════════════

export const APP_TZ = 'Asia/Kolkata';
const DATE_LOCALE = 'en-IN';

const pad = (n) => String(n).padStart(2, '0');

let _viewerTz = 'UTC';
try { _viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { /* noop */ }
export const viewerTimeZone = _viewerTz;
export const viewerIsIST = _viewerTz === APP_TZ || new Date().getTimezoneOffset() === -330;

/** Short label for the viewer's timezone — 'IST' at home, 'GMT+2' / 'EDT' etc. elsewhere. */
export const tzLabel = () => {
  if (viewerIsIST) return 'IST';
  try {
    const part = new Intl.DateTimeFormat('en', { timeZoneName: 'short' })
      .formatToParts(new Date()).find((p) => p.type === 'timeZoneName');
    if (part?.value) return part.value;
  } catch { /* noop */ }
  const off = -new Date().getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  const h = Math.floor(Math.abs(off) / 60);
  const m = Math.abs(off) % 60;
  return `GMT${sign}${h}${m ? `:${pad(m)}` : ''}`;
};

/**
 * Parse a stored datetime into a Date (correct instant on ANY device).
 * Bare wall-clock strings are interpreted as IST; strings that already
 * carry a timezone (Z / ±HH:MM) are respected as-is.
 */
export function parseAppDate(raw) {
  if (!raw) return null;
  if (raw instanceof Date) return raw;
  const s = String(raw);
  const hasTZ = /[zZ]|[+-]\d{2}:?\d{2}$/.test(s);
  const withT = s.length <= 10 ? `${s}T00:00` : s;
  const d = new Date(hasTZ ? withT : `${withT}+05:30`);
  return isNaN(d.getTime()) ? null : d;
}

/** Epoch ms for a stored datetime (0 when missing/invalid). */
export const appTimeMs = (raw) => {
  const d = parseAppDate(raw);
  return d ? d.getTime() : 0;
};

/**
 * Date for calendar-date display/day math. Date-only strings ('YYYY-MM-DD')
 * become LOCAL midnight of that literal date — the shown day never shifts
 * across timezones. Datetime strings fall back to parseAppDate.
 */
export function literalDate(raw) {
  if (!raw) return null;
  const s = String(raw);
  if (s.length <= 10) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  }
  return parseAppDate(raw);
}

/** Format an instant as an IST wall-clock string 'YYYY-MM-DDTHH:mm'. */
export function toISTWall(date) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date);
  const g = (t) => parts.find((p) => p.type === t)?.value || '00';
  const hour = g('hour') === '24' ? '00' : g('hour');
  return `${g('year')}-${g('month')}-${g('day')}T${hour}:${g('minute')}`;
}

/** IST wall-clock 'now' — for comparing against stored wall-clock strings. */
export const nowWall = () => toISTWall(new Date());

/** Stored IST wall-clock → value for <input type="datetime-local"> in the ADMIN'S local tz. */
export function storedToLocalInput(stored) {
  const d = parseAppDate(stored);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** <input type="datetime-local"> value (admin's local tz) → stored IST wall-clock string. */
export function localInputToStored(value) {
  const v = (value || '').trim();
  if (!v) return '';
  const d = new Date(v); // browser interprets datetime-local as local time
  if (isNaN(d.getTime())) return v;
  return toISTWall(d);
}

// ─────────────────────────── display helpers ───────────────────────────
// All render in the viewer's LOCAL timezone (no timeZone override).

function fmtDate(d) {
  return d.toLocaleDateString(DATE_LOCALE, { weekday: 'short', month: 'short', day: 'numeric' });
}
function fmtDateShort(d) {
  return d.toLocaleDateString(DATE_LOCALE, { month: 'short', day: 'numeric' });
}
function fmtTime(d) {
  return d.toLocaleTimeString(DATE_LOCALE, { hour: 'numeric', minute: '2-digit', hour12: true });
}

/** Human event time from stored strings — e.g. "Fri, 22 Aug · 6:00 pm IST–7:00 pm". */
export function formatEventWhen(startAt, endAt, fallback = '') {
  const s = parseAppDate(startAt);
  if (!s) return fallback || startAt || '';
  const hasTime = (startAt || '').length > 10;
  const dateStr = fmtDate(hasTime ? s : (literalDate(startAt) || s));
  const timeStr = hasTime ? `${fmtTime(s)} ${tzLabel()}` : '';
  let out = hasTime ? `${dateStr} · ${timeStr}` : dateStr;
  if (endAt && endAt.length > 10) {
    const e = parseAppDate(endAt);
    if (e) {
      const sameDay = s.toDateString() === e.toDateString();
      out += sameDay ? `–${fmtTime(e)}` : ` → ${fmtDateShort(e)}`;
    }
  }
  return out;
}

/**
 * Format any stored datetime — "Feb 15, 2026 · 3:00 pm IST" (viewer-local).
 * Name kept for compatibility; it now renders in the viewer's timezone.
 */
export function formatIST(raw, { withTime = true } = {}) {
  const dateOnly = typeof raw === 'string' && raw.length <= 10;
  const d = dateOnly ? literalDate(raw) : parseAppDate(raw);
  if (!d) return '';
  const date = d.toLocaleDateString(DATE_LOCALE, { year: 'numeric', month: 'short', day: 'numeric' });
  if (!withTime || dateOnly) return date;
  return `${date} · ${fmtTime(d)} ${tzLabel()}`;
}
export const formatAppTime = formatIST;

/** @deprecated use localInputToStored — kept so old imports don't crash. */
export function istInputToStored(value) {
  return localInputToStored(value);
}

export const IST_TIMEZONE = APP_TZ;
export const IST_LABEL = 'IST (UTC+05:30)';

/** ' UTC' / ' GMT+2' for non-IST viewers, '' at home — for compact rails/chips. */
export const tzSuffix = () => (viewerIsIST ? '' : ` ${tzLabel()}`);
